import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from 'services/repositories/user/user';
import {
  ConfirmationModalIds,
  setConfirmationModalHiddenForever,
} from 'support/helpers/confirmationModals/confirmationModals';
import { Connectors, ValueOf } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { DontShowAgainCheckbox } from 'components/DontShowAgainCheckbox/DontShowAgainCheckbox';
import { Button } from 'components/Form/Button/Button';
import { Translate } from 'components/Translate/Translate';

type SubmitToErpConfirmationModalProps = {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  connector: ValueOf<Connectors>;
};

export const SubmitToErpConfirmationModal: FC<SubmitToErpConfirmationModalProps> = ({
  open,
  onConfirm,
  onCancel,
  connector,
}) => {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const [hideModalForever, setHideModalForever] = useState(false);
  const isOtherConnector = connector !== Connectors.WECLAPP && connector !== Connectors.XENTRAL_V3;
  const translationConnectorSuffix = isOtherConnector
    ? 'other'
    : connector === Connectors.XENTRAL_V3
      ? 'xentral'
      : 'weclapp';

  const handleConfirmApproval = () => {
    if (hideModalForever) {
      setConfirmationModalHiddenForever(ConfirmationModalIds.SubmitToErpConfirmation, currentUser?.id || '');
    }
    onConfirm();
  };
  return (
    <BasePopup open={open} setOpen={onCancel}>
      <BasePopup.Body>
        <h1 className="text-lg font-semibold text-gray-900">
          {t(`paymentReconciliation:submitToErp.confirmationModal.title.${translationConnectorSuffix}`)}
        </h1>
        <div className="mt-2 flex flex-col gap-6">
          <p className=" text-sm text-gray-500">
            <Translate
              i18nKey={`paymentReconciliation:submitToErp.confirmationModal.description.${translationConnectorSuffix}`}
            />
          </p>
          <DontShowAgainCheckbox checked={hideModalForever} setChecked={setHideModalForever} />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={onCancel}
              analyticsId="paymentReconciliation:submitToErp.confirmationModal.cancel"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmApproval}
              analyticsId="paymentReconciliation:submitToErp.confirmationModal.confirm"
            >
              {t('paymentReconciliation:submitToErp.confirmationModal.submitButton')}
            </Button>
          </div>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};

/**
 * i18n
 * t('paymentReconciliation:submitToErp.confirmationModal.title.weclapp')
 * t('paymentReconciliation:submitToErp.confirmationModal.title.xentral')
 * t('paymentReconciliation:submitToErp.confirmationModal.title.other')
 *
 * t('paymentReconciliation:submitToErp.confirmationModal.description.weclapp')
 * t('paymentReconciliation:submitToErp.confirmationModal.description.xentral')
 * t('paymentReconciliation:submitToErp.confirmationModal.description.other')
 */
